import { forwardRef, type HTMLAttributes, type PropsWithChildren } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';
import { motion } from 'framer-motion';

const typographyVariants = cva('leading-none', {
  variants: {
    weight: {
      default: 'font-regular',
      regular: 'font-regular',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
      black: 'font-black',
    },
    size: {
      main: 'text-base sm-h:text-sm',
      small: 'text-sm',
      xSmall: 'text-xs',
    },
  },
});

interface TypographyProps<T extends HTMLElement>
  extends HTMLAttributes<T>,
    VariantProps<typeof typographyVariants> {}

const H1 = forwardRef<HTMLHeadingElement, TypographyProps<HTMLHeadElement>>(
  ({ weight, ...props }, ref) => {
    return (
      <h1
        {...props}
        ref={ref}
        className={cn(
          'font-black text-white text-2xl whitespace-pre-wrap sm-h:text-xl',
          typographyVariants({ weight }),
          props.className,
        )}
      >
        {props.children}
      </h1>
    );
  },
);

const H1Motion = motion.create(H1);

const H2 = forwardRef<HTMLHeadingElement, TypographyProps<HTMLHeadElement>>(
  ({ weight, ...props }, ref) => {
    return (
      <h2
        {...props}
        ref={ref}
        className={cn(
          'font-bold text-white text-xl sm-h:text-lg whitespace-pre-wrap',
          typographyVariants({ weight }),
          props.className,
        )}
      >
        {props.children}
      </h2>
    );
  },
);

const H2Motion = motion.create(H2);

const H3 = forwardRef<HTMLHeadingElement, TypographyProps<HTMLHeadElement>>(
  ({ weight, ...props }, ref) => {
    return (
      <h3
        {...props}
        ref={ref}
        className={cn(
          'font-bold text-white text-lg sm-h:text-base whitespace-pre-wrap',
          typographyVariants({ weight }),
          props.className,
        )}
      >
        {props.children}
      </h3>
    );
  },
);

const StringMain = forwardRef<
  HTMLSpanElement,
  Omit<TypographyProps<HTMLSpanElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <span
      {...props}
      ref={ref}
      className={cn(
        'font-regular text-white',
        typographyVariants({ weight, size: 'main' }),
        props.className,
      )}
    >
      {props.children}
    </span>
  );
});

const StringSmall = forwardRef<
  HTMLSpanElement,
  Omit<TypographyProps<HTMLSpanElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <span
      {...props}
      ref={ref}
      className={cn(
        'font-regular text-white',
        typographyVariants({ weight, size: 'small' }),
        props.className,
      )}
    >
      {props.children}
    </span>
  );
});

const StringSmallMotion = motion.create(StringSmall);

const StringXSmall = forwardRef<
  HTMLSpanElement,
  Omit<TypographyProps<HTMLSpanElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <span
      {...props}
      ref={ref}
      className={cn(
        'font-regular text-white',
        typographyVariants({ weight, size: 'xSmall' }),
        props.className,
      )}
    >
      {props.children}
    </span>
  );
});

const TextMain = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps<HTMLParagraphElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <p
      {...props}
      ref={ref}
      className={cn(
        'font-regular text-white',
        typographyVariants({ weight, size: 'main' }),
        props.className,
      )}
    >
      {props.children}
    </p>
  );
});

const TextSmall = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps<HTMLParagraphElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <p
      {...props}
      ref={ref}
      className={cn(
        'font-regular text-white',
        typographyVariants({ weight, size: 'small' }),
        props.className,
      )}
    >
      {props.children}
    </p>
  );
});

const TextXSmall = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps<HTMLParagraphElement>, 'size'>
>(({ weight, ...props }, ref) => {
  return (
    <p
      {...props}
      ref={ref}
      className={cn(
        'font-regular',
        typographyVariants({ weight, size: 'xSmall' }),
        props.className,
      )}
      style={{ color: '#000' }}
    >
      {props.children}
    </p>
  );
});

interface HugeTextProps
  extends HTMLAttributes<HTMLSpanElement>,
    PropsWithChildren {}

const HugeText = forwardRef<HTMLSpanElement, HugeTextProps>(
  ({ children, ...props }, ref) => {
    return (
      <span ref={ref} {...props} className={cn('huge-text', props.className)}>
        {children}
      </span>
    );
  },
);

/* eslint-disable react-refresh/only-export-components */
export {
  H1,
  H1Motion,
  H2,
  H2Motion,
  H3,
  HugeText,
  StringMain,
  StringSmall,
  StringSmallMotion,
  StringXSmall,
  TextMain,
  TextSmall,
  TextXSmall,
};
